import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardHeader } from '@/components/base/Card';
import { recentSessionNotes } from '@/mocks/dashboard';

export default function SessionNotesList() {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const moodConfig = {
    positive:    { icon: 'ri-emotion-happy-line',   bg: 'bg-semantic-success-bg', text: 'text-semantic-success' },
    neutral:     { icon: 'ri-emotion-normal-line',  bg: 'bg-background-200',       text: 'text-foreground-500' },
    challenging: { icon: 'ri-emotion-sad-line',      bg: 'bg-semantic-warning-bg', text: 'text-semantic-warning' },
  };

  const moodLabels: Record<string, string> = {
    positive: t('dashboard.positive'),
    neutral: t('dashboard.neutral'),
    challenging: t('dashboard.challenging'),
  };

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 p-5">
      <CardHeader
        title={t('dashboard.recentSessionNotes')}
        subtitle={t('dashboard.notesNeedFollowUp', {
          total: recentSessionNotes.length,
          challenging: recentSessionNotes.filter(n => n.mood === 'challenging').length
        })}
        icon="ri-sticky-note-line"
        iconBg="bg-secondary-100"
        action={
          <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-label font-medium text-primary-600 bg-primary-100 rounded-lg hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" />
            {t('dashboard.newNote')}
          </button>
        }
      />

      <div className="space-y-2">
        {recentSessionNotes.map((n) => {
          const mood = moodConfig[n.mood];
          const isExpanded = expandedId === n.id;

          return (
            <div
              key={n.id}
              className="border border-background-200 rounded-lg overflow-hidden transition-all duration-200"
            >
              {/* Row header */}
              <button
                onClick={() => setExpandedId(isExpanded ? null : n.id)}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-label font-medium flex items-center gap-1 ${mood.bg} ${mood.text}`}>
                  <i className={`${mood.icon} text-[10px]`} />
                  {moodLabels[n.mood]}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-label font-medium text-foreground-800">{n.student}</span>
                  <span className="text-xs text-foreground-400 font-label ml-2">{n.domain}</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 text-xs text-foreground-400 font-label flex-shrink-0">
                  <span>{n.date}</span>
                  <span className="hidden sm:inline">{n.duration}</span>
                  <i className={isExpanded ? 'ri-arrow-up-s-line text-foreground-400' : 'ri-arrow-down-s-line text-foreground-400'} />
                </div>
              </button>

              {/* Expanded note */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-0 border-t border-background-200">
                  <p className="text-sm text-foreground-600 leading-relaxed mt-3">{n.note}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <button className="flex items-center gap-1 px-2.5 py-1 text-xs font-label font-medium text-foreground-500 bg-background-100 rounded-md hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-edit-line" />
                      {t('common.edit')}
                    </button>
                    <button className="flex items-center gap-1 px-2.5 py-1 text-xs font-label font-medium text-primary-600 bg-primary-100 rounded-md hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-check-line" />
                      {t('dashboard.markReviewed')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}